import PropTypes from 'prop-types';
import { useState, useContext } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { handleUserRefetch } from '../helpers/sbCreatorsRootUserRefetch';

/* Components */
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbRespCreatorsComponents as SbRespCreatorsRootComponents } from '../SbRespCreatorsComponents/SbRespCreatorsComponents';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';

const SbRespCreatorsLayoutComponent = props => {
    const [isClient, setIsClient] = useState(false);
    const { isTablet } = useContext(ServerVarsContext);
    const { viewer, reinitTracking, relay } = props;
    const user = viewer.user || null;
    const { itemSearch } = viewer;

    return (
        <RecentlyViewedItemsProvider maxItems={isTablet ? 10 : 12} itemsPerPage={isTablet ? 5 : 6}>
            <SharedUserAndRelayVarLoader
                onUserRefetch={handleUserRefetch(relay, setIsClient)}
                environment={relay.environment}
            />
            <CurrencyProvider
                render={({ currency }) => (
                    <>
                        <SbSharedTrackingContainer
                            user={user}
                            itemSearch={itemSearch}
                            reinitTracking={reinitTracking}
                            refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                        />
                        <SbSharedHead itemSearch={itemSearch} />
                        <SbRespCreatorsRootComponents
                            viewer={viewer}
                            itemSearch={itemSearch}
                            user={user}
                            currency={currency}
                            isClient={isClient}
                        />
                    </>
                )}
            />
        </RecentlyViewedItemsProvider>
    );
};

SbRespCreatorsLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
};

SbRespCreatorsLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

const mapStateToProps = state => {
    const { intlVariables, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespCreatorsLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespCreatorsLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespCreatorsLayout_viewer on Viewer {
                ...SbRespCreatorsComponents_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespCreatorsComponents_user
                }
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                ) {
                    ...SbSharedHead_itemSearch
                    ...SbRespCreatorsComponents_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
            }
        `,
    },
    graphql`
        query SbRespCreatorsLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
        ) {
            viewer {
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespCreatorsComponents_user
                }
            }
        }
    `
);
